import HelloSign from 'hellosign-embedded';
import _get from 'lodash/get';
import { call, put } from 'redux-saga/effects';

import { fetchSigningUrl, fetchPdfUrl, validateToken } from 'modules/api/emailSignatures';
import { isDevEnv, HELLO_SIGN_CLIENT_ID } from 'constants/env';
import { actions as brandingActions } from 'modules/branding';

import * as hellosignActions from '../actions';

/**
 * Load the HelloSign iFrame based on the given signing URL.
 *
 * @method loadHellosignIframe
 * @type   {Generator}
 * @param  {string}            url        The embedded signing url
 * @param  {Func}              redirectTo Callback invoked after the user has finished signing
 */
export function* loadHellosignIframe(url, redirectTo) {
    try {
        // Create the HelloSign client.
        const HelloSignClient = yield new HelloSign({
            clientId   : HELLO_SIGN_CLIENT_ID,
            allowCancel: false
        });
        // Listen to the `finish` event.
        HelloSignClient.on('finish', () => {
            typeof redirectTo === 'function' && redirectTo();
        });
        // Load the iFrame.
        HelloSignClient.open(url, {
            debug                 : isDevEnv,
            // TODO: This should be set to the value of `isDevEnv`
            // to not skip the domain verification when we're running in `prod`.
            skipDomainVerification: true
        });
    } catch (error) {
        // Throw the error.
        throw error;
    }
}

/**
 * Fetch the signing URL for the HelloSign document.
 *
 * @method fetchSigningUrlSaga
 * @type   {Generator}
 * @param  {Object}            action The redux action
 * @return {Object}
 */
export function* fetchSigningUrlSaga(action) {
    try {
        // Extract the `token` from the action.
        const { payload: token } = action;
        // Make the API call to fetch the HelloSign embedded signing URL.
        const apiResp = yield call(fetchSigningUrl, token);
        // Extract the `signatureInfo` from the `apiResp`.
        const signatureInfo = _get(apiResp, 'data.signature');
        // Extract the `embeddedSigningURL`.
        const embeddedSigningURL = _get(signatureInfo, 'embeddedSigningURL');
        // Fail the request if the signing url is not available.
        if (!embeddedSigningURL) {
            throw new Error('Unable to fetch the signing URL. Please try again.');
        }
        // Dispatch the success action.
        yield put(hellosignActions.fetchSigningUrl.success(signatureInfo));
    } catch (error) {
        // Dispatch the error action.
        yield put(hellosignActions.fetchSigningUrl.error(error));
        // Return the error.
        return error;
    }
}

/**
 * Fetch the PDF Blob for the HelloSign document.
 *
 * @method fetchPdfUrlSaga
 * @type   {Generator}
 * @param  {Object}            action The redux action
 * @return {Object}
 */
export function* fetchPdfUrlSaga(action) {
    try {
        // Extract the `token` from the action.
        const { payload: token } = action;
        // Make the API call to fetch the HelloSign pdf Blob.
        const pdfResp = yield call(fetchPdfUrl, token);
        // Convert response in a Blob file.
        const fileBlob = new Blob([pdfResp.data], { type: 'application/pdf' });
        // Dispatch the success action.
        yield put(
            hellosignActions.fetchPdfUrl.success({
                fileBlob
            })
        );
    } catch (error) {
        // Dispatch the error action.
        yield put(hellosignActions.fetchPdfUrl.error(error));
        // Return the error.
        return error;
    }
}

/**
 * Fetch the PDF Blob and the signing URL for the HelloSign document.
 *
 * @method fetchPdfUrlSaga
 * @type   {Generator}
 * @param  {Object}            action The redux action
 * @return {Object}
 */
export function* fetchPdfAndSigningUrlSaga(action) {
    try {
        // Extract the `token` from the action.
        const { payload: token } = action;
        // Make the API call to fetch the HelloSign pdf Blob.
        const pdfResp = yield call(fetchPdfUrl, token);
        // Convert response in a Blob file.
        const fileBlob = new Blob([pdfResp.data], { type: 'application/pdf' });
        // Make the API call to fetch the HelloSign embedded signing URL.
        const signingResp = yield call(fetchSigningUrl, token);
        // Extract the `signatureInfo` from the `signingResp`.
        const signatureInfo = _get(signingResp, 'data.signature');
        // Extract the `embeddedSigningURL`.
        const embeddedSigningURL = _get(signatureInfo, 'embeddedSigningURL');
        // Fail the request if the signing url is not available.
        if (!embeddedSigningURL) {
            throw new Error('Unable to fetch the signing URL. Please try again.');
        }
        // Dispatch the success action.
        yield put(
            hellosignActions.fetchPdfAndSigningUrl.success({
                info: signatureInfo,
                fileBlob
            })
        );
    } catch (error) {
        // Dispatch the error action.
        yield put(hellosignActions.fetchPdfAndSigningUrl.error(error));
        // Return the error.
        return error;
    }
}

/**
 * Fetch the signing and pdf URL for the HelloSign document.
 *
 * @method loadSigningIframeSaga
 * @type   {Generator}
 * @param  {Object}                   action The redux action
 * @return {Generator}
 */
export function* loadSigningIframeSaga(action) {
    try {
        // Extract the `token` from the action.
        const {
            payload: { token, redirectTo }
        } = action;
        // Make the API call to fetch the HelloSign embedded signing URL.
        const signingResp = yield call(fetchSigningUrl, token);
        // Extract the `signatureInfo` from the `signingResp`.
        const signatureInfo = _get(signingResp, 'data.signature');
        // Extract the `embeddedSigningURL`.
        const embeddedSigningURL = _get(signatureInfo, 'embeddedSigningURL');
        // Fail the request if the signing url is not available.
        if (!embeddedSigningURL) {
            throw new Error('Unable to fetch the signing URL. Please try again.');
        }
        // Load the HelloSign iFrame.
        yield call(loadHellosignIframe, embeddedSigningURL, redirectTo);
        // Dispatch the success action.
        yield put(hellosignActions.loadSigningIframe.success());
    } catch (error) {
        // Dispatch the error action.
        yield put(hellosignActions.loadSigningIframe.error(error));
        // Return the error.
        return error;
    }
}

/**
 * Fetch the validated token
 *
 * @method validateTokenSaga
 * @type   {Generator}
 * @param  {Object}                   action The redux action
 * @return {Generator}
 */
export function* validateTokenSaga(action) {
    try {
        // Extract the `token` from the action.
        const { payload: token } = action;
        // Make the API call to validate token.
        const apiResp = yield call(validateToken, token);
        // Get brokerId to get branding
        const partnerId = _get(apiResp, 'data.signature.partnerId');
        // Update the branding for the broker.
        yield put(brandingActions.fetchBranding(partnerId));
        // Extract the `signatureInfo` from the `signingResp`.
        const info = _get(apiResp, 'data.signature');
        // Dispatch the success action.
        yield put(
            hellosignActions.validateToken.success({
                info
            })
        );
    } catch (error) {
        // Reset the branding since there was an error in validating the token.
        yield put(brandingActions.resetBranding());
        // Dispatch the error action.
        yield put(hellosignActions.validateToken.error(error));
        // Return the error.
        return error;
    }
}
